import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress, 
  Paper, 
  List, 
  ListItem, 
  ListItemText,
  Tabs,
  Tab,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { 
  UploadFile as UploadIcon, 
  Check as CheckIcon,
  Link as LinkIcon 
} from '@mui/icons-material';
import { api } from '../services/api';
import { useQueryClient } from 'react-query';

// Define the ref interface
export interface FileUploadSectionRef {
  resetData: () => void;
}

interface FileUploadProps {
  onUploadSuccess: () => void;
}

// Tab panel for the upload tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Define the component with React.forwardRef
const FileUploadSection = forwardRef<FileUploadSectionRef, FileUploadProps>(function FileUploadSection(props, ref) {
  const { onUploadSuccess } = props;
  const [tabValue, setTabValue] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [selectedConference, setSelectedConference] = useState('');
  const [customEndpointEnabled, setCustomEndpointEnabled] = useState(false);
  const queryClient = useQueryClient();
  
  // Available conferences
  const conferences = [
    { label: 'AAOS 2025', value: '2025 AAOS' },
    { label: 'ASCRS 2025', value: '2025 ASCRS' },
    { label: 'AAAAI 2025', value: '2025 AAAAI' },
    { label: 'ISC 2025', value: '2025 ISC' },
    { label: 'HRS 2025', value: '2025 HRS' },
    { label: 'ARVO 2025', value: '2025 ARVO' },
    { label: 'APA 2025', value: '2025 APA' },
    { label: 'TCT 2025', value: '2025 TCT' },
    { label: 'ACR 2025', value: '2025 ACR' }
  ];
  
  // Base URL
  const baseEndpointUrl = 'https://abtsolute.roomhandler.com/HotelReportHandler.ashx/';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Clear any errors when switching tabs
    setUploadError(null);
  };
  
  const handleConferenceChange = (event: any) => {
    const conference = event.target.value as string;
    setSelectedConference(conference);
    
    if (conference) {
      setEndpointUrl(baseEndpointUrl + conference);
    } else {
      setEndpointUrl('');
    }
    
    // Disable custom endpoint when changing conference
    setCustomEndpointEnabled(false);
  };
  
  const toggleCustomEndpoint = () => {
    setCustomEndpointEnabled(!customEndpointEnabled);
  };
  
  const handleEndpointUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndpointUrl(event.target.value);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Filter for only CSV files
    const csvFiles = acceptedFiles.filter(
      file => file.type === 'text/csv' || file.name.endsWith('.csv')
    );
    
    if (csvFiles.length < acceptedFiles.length) {
      setUploadError('Only CSV files are allowed. Some files were rejected.');
    } else {
      setUploadError(null);
    }
    
    setFiles(csvFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: true,
  });

  const handleUpload = async () => {
    if (files.length === 0) {
      setUploadError('Please select at least one CSV file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const result = await api.files.upload(files);
      
      if (result.success) {
        queryClient.invalidateQueries('dataSummary');
        onUploadSuccess();
      } else {
        setUploadError(`Upload failed: ${result.message}`);
      }
    } catch (error) {
      setUploadError(`Error uploading files: ${error}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFetchFromEndpoint = async () => {
    if (!endpointUrl) {
      setUploadError('Please enter an endpoint URL');
      return;
    }

    // Simple URL validation
    try {
      new URL(endpointUrl);
    } catch (e) {
      setUploadError('Please enter a valid URL');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const result = await api.files.fetchFromEndpoint(endpointUrl);
      
      if (result.success) {
        queryClient.invalidateQueries('dataSummary');
        onUploadSuccess();
      } else {
        setUploadError(`Fetch failed: ${result.message}`);
      }
    } catch (error) {
      setUploadError(`Error fetching from endpoint: ${error}`);
    } finally {
      setUploading(false);
    }
  };
  
  const handleReset = () => {
    // Reset all state to initial values
    setFiles([]);
    setEndpointUrl('');
    setSelectedConference('');
    setUploadError(null);
    setCustomEndpointEnabled(false);
    setTabValue(0); // Reset to first tab
  };

  // Expose the reset function via ref
  useImperativeHandle(ref, () => ({
    resetData: handleReset
  }));

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Load Data
      </Typography>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          centered
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Upload Files" icon={<UploadIcon />} iconPosition="start" />
          <Tab label="Fetch from Endpoint" icon={<LinkIcon />} iconPosition="start" />
        </Tabs>

        {/* File Upload Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Upload one or more CSV files containing booking data. The files should share the same format.
          </Typography>

          <Paper
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              mb: 3,
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">
              {isDragActive ? 'Drop CSV files here' : 'Drag & drop CSV files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse files
            </Typography>
          </Paper>

          {files.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Selected Files ({files.length})
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                <List dense>
                  {files.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          {uploadError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {uploadError}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        </TabPanel>

        {/* Endpoint Fetch Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Fetch data from an API endpoint that returns JSON. The data will be converted to CSV format automatically.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="conference-select-label">Select Conference</InputLabel>
            <Select
              labelId="conference-select-label"
              id="conference-select"
              value={selectedConference}
              onChange={handleConferenceChange}
              label="Select Conference"
              disabled={uploading}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {conferences.map((conference) => (
                <MenuItem key={conference.value} value={conference.value}>
                  {conference.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Choose a conference to generate the endpoint URL
            </FormHelperText>
          </FormControl>
          
          {selectedConference && (
            <Box>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <TextField
                  label="Endpoint URL"
                  variant="outlined"
                  value={endpointUrl}
                  onChange={handleEndpointUrlChange}
                  InputProps={{
                    readOnly: !customEndpointEnabled,
                  }}
                  disabled={uploading}
                />
                <FormHelperText>
                  {customEndpointEnabled 
                    ? "Edit the custom endpoint URL" 
                    : "This URL will be used to fetch the data"}
                </FormHelperText>
              </FormControl>
              
              <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button
                  size="small"
                  color={customEndpointEnabled ? "primary" : "secondary"}
                  onClick={toggleCustomEndpoint}
                  variant={customEndpointEnabled ? "contained" : "outlined"}
                  sx={{ mt: 1 }}
                >
                  {customEndpointEnabled ? "Disable Custom URL" : "Enable Custom URL"}
                </Button>
              </Box>
            </Box>
          )}

          {uploadError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {uploadError}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleFetchFromEndpoint}
            disabled={!endpointUrl || uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
          >
            {uploading ? 'Fetching...' : 'Fetch Data'}
          </Button>
        </TabPanel>
      </Paper>
    </Box>
  );
});

export default FileUploadSection;
